import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';

const initialFormData = {
    comunicadoId: 0,
    fecha: new Date(),
    asunto: '',
    comunicado: '',
    urgente: false,
    activo: true,
};

const Comunicados = () => {
    const toast = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();

    const [formData, setFormData] = useState(initialFormData);
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [comunicados, setComunicados] = useState([]);

    const handleChange = (campo, valor) => setFormData(prev => ({ ...prev, [campo]: valor }));

    const fetchComunicados = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/comunicados/GetComunicados?administracionId=${administracionId}&barrioId=${barrio.id}`
            );
            setComunicados(data);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los comunicados', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, HOST_API, administracionId]);

    useEffect(() => { fetchComunicados(); }, [fetchComunicados]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.asunto.trim() || !formData.comunicado.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Asunto y comunicado son obligatorios', life: 3000 });
            return;
        }
        try {
            await axios.post(`${HOST_API}api/comunicados/SaveComunicado`, {
                ...formData,
                barrioId: barrio.id,
                administracionId,
            });
            setVisible(false);
            fetchComunicados();
            toast.current.show({ severity: 'success', summary: 'Guardado', detail: 'Comunicado guardado correctamente', life: 2500 });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el comunicado', life: 3000 });
        }
    };

    const handleEdit = (row) => {
        setFormData({
            ...row,
            fecha: row.fecha ? new Date(row.fecha) : new Date(),
        });
        setVisible(true);
    };

    const handleDelete = (id) => {
        confirmDialog({
            message: '¿Estás seguro de eliminar este comunicado?',
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/comunicados/DeleteComunicado?comunicadoId=${id}&administracionId=${administracionId}`);
                    fetchComunicados();
                    toast.current.show({ severity: 'success', summary: 'Eliminado', detail: 'Comunicado eliminado', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar', life: 3000 });
                }
            },
        });
    };

    const pillStyle = (bg, color, border) => ({
        background: bg, color, border: `1px solid ${border}`,
        fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
    });

    const urgenteTemplate = (row) => {
        const s = row.urgente
            ? { bg: '#fef3f2', color: '#f04438', border: '#fecdca', label: 'Urgente' }
            : { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', label: 'Normal' };
        return <span style={pillStyle(s.bg, s.color, s.border)}>{s.label}</span>;
    };

    const fechaTemplate = (row) =>
        row.fecha ? new Date(row.fecha).toLocaleDateString('es-AR') : '';

    const comunicadoTemplate = (row) =>
        row.comunicado?.length > 80 ? row.comunicado.substring(0, 80) + '…' : row.comunicado;

    return (
        <>
            <h4>Comunicados</h4>
            <br />

            <ConfirmDialog />
            <Toast ref={toast} />
            <LoadingOverlay visible={loading} />

            <div className="flex align-items-center gap-3 mb-3">
                <Button
                    label="Nuevo Comunicado"
                    icon="pi pi-plus"
                    onClick={() => { setFormData(initialFormData); setVisible(true); }}
                />
            </div>

            <Dialog
                header={formData.comunicadoId ? 'Editar Comunicado' : 'Nuevo Comunicado'}
                visible={visible}
                style={{ width: '55vw' }}
                breakpoints={{ '960px': '100vw' }}
                modal
                onHide={() => setVisible(false)}
            >
                <form className="p-fluid grid" onSubmit={handleSubmit}>
                    <div className="col-12">
                        <div className="col-12 flex flex-wrap gap-3 mt-3">

                            <div className="w-full md:w-4">
                                <label>Fecha</label>
                                <Calendar
                                    value={formData.fecha}
                                    dateFormat="dd/mm/yy"
                                    onChange={(e) => handleChange('fecha', e.value)}
                                    showIcon
                                    className="p-inputtext-sm w-full"
                                />
                            </div>

                            <div className="w-full">
                                <label>Asunto</label>
                                <InputText
                                    value={formData.asunto}
                                    onChange={(e) => handleChange('asunto', e.target.value)}
                                    className="p-inputtext-sm w-full"
                                    maxLength={200}
                                />
                            </div>

                            <div className="w-full">
                                <label>Comunicado</label>
                                <InputTextarea
                                    value={formData.comunicado}
                                    onChange={(e) => handleChange('comunicado', e.target.value)}
                                    rows={6}
                                    autoResize
                                    className="w-full"
                                />
                            </div>

                            <div className="w-full flex align-items-center gap-2 mt-1">
                                <Checkbox
                                    inputId="urgente"
                                    checked={formData.urgente}
                                    onChange={(e) => handleChange('urgente', e.checked)}
                                />
                                <label htmlFor="urgente">Urgente</label>
                            </div>

                            <div className="col-12 mt-3">
                                <Button label="Guardar" icon="pi pi-save" type="submit" />
                            </div>

                        </div>
                    </div>
                </form>
            </Dialog>

            <div className="grid">
                <div className="col-12">
                    <DataTable
                        value={comunicados}
                        paginator
                        rows={10}
                        showGridlines
                        rowsPerPageOptions={[5, 10, 25]}
                        responsiveLayout="scroll"
                        emptyMessage="No hay comunicados cargados"
                        className="p-datatable-gridlines bg-white"
                        sortField="fecha"
                        sortOrder={-1}
                    >
                        <Column field="fecha" header="Fecha" sortable body={fechaTemplate} style={{ width: '8rem' }} />
                        <Column field="urgente" header="Tipo" body={urgenteTemplate} style={{ width: '7rem' }} />
                        <Column field="asunto" header="Asunto" sortable />
                        <Column field="comunicado" header="Comunicado" body={comunicadoTemplate} />
                        <Column
                            header="Estado"
                            style={{ width: '8rem' }}
                            body={(row) => {
                                const s = row.activo
                                    ? { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5', label: 'Activo' }
                                    : { bg: '#fef3f2', color: '#f04438', border: '#fecdca', label: 'No Activo' };
                                return <span style={pillStyle(s.bg, s.color, s.border)}>{s.label}</span>;
                            }}
                        />
                        <Column
                            header="Acciones"
                            style={{ width: '8rem' }}
                            body={(row) => (
                                <div className="flex gap-2 justify-content-center">
                                    <Button icon="pi pi-pencil" rounded text severity="warning" onClick={() => handleEdit(row)} tooltip="Editar" />
                                    <Button icon="pi pi-trash" rounded className="btn-delete" onClick={() => handleDelete(row.comunicadoId)} tooltip="Eliminar" />
                                </div>
                            )}
                        />
                    </DataTable>
                </div>
            </div>
        </>
    );
};

export default Comunicados;
